import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useListUids } from "@/lib/hooks";

const emptyForm = {
  title: "", subtitle: "", deck: "", slug: "",
  guest_name: "", guest_role: "", company: "", operator_slug: "", industry_slug: "",
  location: "", topics: [], format: "written", reading_time: 10,
  featured: false, editors_pick: false,
  hero_image: "", thumbnail: "",
  executive_summary: "", why_it_matters: "",
  key_takeaways: [], body: [], pull_quotes: [],
  operating_system: {},
  published_at: "",
};

export default function AdminInterviewEdit() {
  const { slug } = useParams();
  const nav = useNavigate();
  const isNew = !slug;
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [operators, setOperators] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [topics, setTopics] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/operators"),
      api.get("/industries"),
      api.get("/topics"),
    ]).then(([o, i, t]) => {
      setOperators(o.data);
      setIndustries(i.data);
      setTopics(t.data);
    });
  }, []);

  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    api.get(`/interviews/${slug}`)
      .then((r) => setForm({
        ...emptyForm,
        ...r.data,
        key_takeaways: r.data.key_takeaways || [],
        body: r.data.body || [],
        pull_quotes: r.data.pull_quotes || [],
        operating_system: r.data.operating_system || {},
        topics: r.data.topics || [],
      }))
      .catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
  }, [slug, isNew]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setOS = (k, v) => setForm((f) => ({ ...f, operating_system: { ...f.operating_system, [k]: v } }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      // clean empties
      if (!payload.published_at) delete payload.published_at;
      if (isNew) {
        const r = await api.post("/admin/interviews", payload);
        toast.success("Created");
        nav(`/admin/interviews/${r.data.slug}`);
      } else {
        await api.put(`/admin/interviews/${slug}`, payload);
        toast.success("Saved");
      }
    } catch (err) {
      toast.error("Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="py-24 text-center">Loading...</div>;

  return (
    <div data-testid="admin-interview-edit">
      <Link to="/admin/interviews" className="font-ui text-xs font-bold uppercase tracking-widest text-oc-ink-muted hover:text-oc-blue">
        ← All interviews
      </Link>
      <h1 className="font-editorial text-4xl text-oc-ink mt-4">{isNew ? "New interview" : form.title || "Edit interview"}</h1>

      <form onSubmit={save} className="mt-10 max-w-4xl space-y-10">
        <Group title="Headline">
          <Text label="Title" value={form.title} onChange={(v) => set("title", v)} required testId="ai-title" />
          <Text label="Subtitle" value={form.subtitle} onChange={(v) => set("subtitle", v)} testId="ai-subtitle" />
          <Text label="Deck / short excerpt" value={form.deck} onChange={(v) => set("deck", v)} testId="ai-deck" />
          <Text label="Slug (auto-generated if empty)" value={form.slug} onChange={(v) => set("slug", v)} testId="ai-slug" />
        </Group>

        <Group title="Guest">
          <Text label="Guest name" value={form.guest_name} onChange={(v) => set("guest_name", v)} required testId="ai-guest" />
          <Text label="Guest role" value={form.guest_role} onChange={(v) => set("guest_role", v)} testId="ai-role" />
          <Text label="Company" value={form.company} onChange={(v) => set("company", v)} testId="ai-company" />
          <Text label="Location" value={form.location} onChange={(v) => set("location", v)} testId="ai-location" />
          <Select label="Operator (linked profile)" value={form.operator_slug} onChange={(v) => set("operator_slug", v)}
            options={[{ value: "", label: ",  none , " }, ...operators.map((o) => ({ value: o.slug, label: o.name }))]}
            testId="ai-operator"
          />
          <Select label="Industry" value={form.industry_slug} onChange={(v) => set("industry_slug", v)}
            options={[{ value: "", label: ",  none , " }, ...industries.map((i) => ({ value: i.slug, label: i.name }))]}
            testId="ai-industry"
          />
        </Group>

        <Group title="Meta">
          <Text label="Hero image URL" value={form.hero_image} onChange={(v) => set("hero_image", v)} testId="ai-hero" />
          <Text label="Thumbnail image URL" value={form.thumbnail} onChange={(v) => set("thumbnail", v)} testId="ai-thumb" />
          <Text label="Reading time (min)" type="number" value={form.reading_time} onChange={(v) => set("reading_time", parseInt(v || 0, 10))} testId="ai-time" />
          <Text label="Publication date (ISO)" value={form.published_at || ""} onChange={(v) => set("published_at", v)} placeholder="2025-11-14T09:00:00+00:00" testId="ai-date" />
          <div className="col-span-full flex gap-6">
            <Toggle label="Featured" value={form.featured} onChange={(v) => set("featured", v)} testId="ai-featured" />
            <Toggle label="Editor's pick" value={form.editors_pick} onChange={(v) => set("editors_pick", v)} testId="ai-pick" />
          </div>
        </Group>

        <Group title="Topics">
          <div className="col-span-full flex flex-wrap gap-2">
            {topics.map((t) => {
              const active = form.topics.includes(t.slug);
              return (
                <button
                  type="button"
                  key={t.slug}
                  onClick={() => set("topics", active ? form.topics.filter((s) => s !== t.slug) : [...form.topics, t.slug])}
                  className={`oc-chip ${active ? "active" : ""}`}
                >
                  {t.name}
                </button>
              );
            })}
          </div>
        </Group>

        <Group title="Editorial body">
          <TextArea label="Executive summary" value={form.executive_summary} onChange={(v) => set("executive_summary", v)} rows={4} testId="ai-summary" />
          <TextArea label="Why this conversation matters" value={form.why_it_matters} onChange={(v) => set("why_it_matters", v)} rows={3} testId="ai-why" />
          <ListEditor label="Key takeaways" items={form.key_takeaways} onChange={(v) => set("key_takeaways", v)} testId="ai-takeaways" />
          <BodyEditor sections={form.body} onChange={(v) => set("body", v)} />
          <ListEditor label="Pull quotes" items={form.pull_quotes} onChange={(v) => set("pull_quotes", v)} placeholder="Quote" testId="ai-quotes" />
        </Group>

        <Group title="The Operating System">
          {["hiring_philosophy", "decision_framework", "non_negotiables", "metrics_watched", "biggest_mistake", "principle_to_pass_on"].map((k) => (
            <TextArea key={k} label={k.replace(/_/g, " ")} value={form.operating_system?.[k] || ""} onChange={(v) => setOS(k, v)} rows={2} testId={`ai-os-${k}`} />
          ))}
        </Group>

        <div className="pt-6 flex gap-3">
          <button
            data-testid="ai-save"
            disabled={saving}
            className="h-12 px-6 bg-oc-blue text-white font-ui text-xs font-bold uppercase tracking-widest hover:bg-oc-blue-hover disabled:opacity-60"
          >
            {saving ? "Saving..." : isNew ? "Create interview" : "Save changes"}
          </button>
          <Link to="/admin/interviews" className="h-12 px-6 border border-oc-ink font-ui text-xs font-bold uppercase tracking-widest flex items-center">
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}

function Group({ title, children }) {
  return (
    <div className="pb-8 border-b border-oc-hairline">
      <div className="eyebrow text-oc-coral mb-6">{title}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">{children}</div>
    </div>
  );
}
function Text({ label, value, onChange, required, type = "text", placeholder, testId }) {
  return (
    <label className="block">
      <span className="eyebrow text-oc-ink-muted mb-2 block">{label}{required && " *"}</span>
      <input
        data-testid={testId}
        type={type}
        required={required}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full h-11 px-3 bg-white border border-oc-hairline focus:border-oc-ink outline-none font-ui text-sm"
      />
    </label>
  );
}
function TextArea({ label, value, onChange, rows = 4, testId }) {
  return (
    <label className="block col-span-full">
      <span className="eyebrow text-oc-ink-muted mb-2 block">{label}</span>
      <textarea
        data-testid={testId}
        rows={rows}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 bg-white border border-oc-hairline focus:border-oc-ink outline-none font-ui text-sm resize-y"
      />
    </label>
  );
}
function Select({ label, value, onChange, options, testId }) {
  return (
    <label className="block">
      <span className="eyebrow text-oc-ink-muted mb-2 block">{label}</span>
      <select
        data-testid={testId}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-11 px-3 bg-white border border-oc-hairline focus:border-oc-ink outline-none font-ui text-sm"
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}
function Toggle({ label, value, onChange, testId }) {
  return (
    <label className="inline-flex items-center gap-3 cursor-pointer">
      <input
        data-testid={testId}
        type="checkbox"
        checked={!!value}
        onChange={(e) => onChange(e.target.checked)}
        className="w-5 h-5 accent-oc-blue"
      />
      <span className="font-ui text-sm font-semibold">{label}</span>
    </label>
  );
}
function ListEditor({ label, items, onChange, placeholder = "Item", testId }) {
  const { keyOf, add, remove, update } = useListUids(items, onChange);
  return (
    <div className="col-span-full">
      <span className="eyebrow text-oc-ink-muted mb-3 block">{label}</span>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={keyOf(i)} className="flex gap-2">
            <input
              data-testid={`${testId}-${i}`}
              value={it}
              onChange={(e) => update(i, e.target.value)}
              placeholder={placeholder}
              className="flex-1 h-10 px-3 bg-white border border-oc-hairline focus:border-oc-ink outline-none font-ui text-sm"
            />
            <button type="button" onClick={() => remove(i)} className="px-3 h-10 border border-oc-hairline text-oc-coral font-ui text-xs">
              Remove
            </button>
          </div>
        ))}
        <button type="button" onClick={() => add("")} className="h-10 px-4 border border-oc-ink font-ui text-xs font-bold uppercase tracking-widest">
          + Add
        </button>
      </div>
    </div>
  );
}
function BodyEditor({ sections, onChange }) {
  const { keyOf, add, remove, update } = useListUids(sections, onChange);
  return (
    <div className="col-span-full">
      <span className="eyebrow text-oc-ink-muted mb-3 block">Interview body, sections</span>
      <div className="space-y-4">
        {sections.map((s, i) => (
          <div key={keyOf(i)} className="border border-oc-hairline p-4 bg-oc-sand/50">
            <div className="flex items-center justify-between mb-3">
              <div className="font-ui text-xs font-bold uppercase tracking-widest text-oc-ink-muted">Section {i + 1}</div>
              <button type="button" onClick={() => remove(i)} className="font-ui text-xs text-oc-coral">Remove</button>
            </div>
            <input
              value={s.heading || ""}
              onChange={(e) => update(i, { ...s, heading: e.target.value })}
              placeholder="Section heading"
              className="w-full h-10 px-3 bg-white border border-oc-hairline outline-none font-ui text-sm mb-3"
            />
            <textarea
              rows={5}
              value={s.content || ""}
              onChange={(e) => update(i, { ...s, content: e.target.value })}
              placeholder="Section content..."
              className="w-full px-3 py-2 bg-white border border-oc-hairline outline-none font-ui text-sm"
            />
          </div>
        ))}
        <button type="button" onClick={() => add({ heading: "", content: "" })} className="h-10 px-4 border border-oc-ink font-ui text-xs font-bold uppercase tracking-widest">
          + Add section
        </button>
      </div>
    </div>
  );
}
